import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Step1BasicInfo } from "@/components/profile-creation/Step1BasicInfo";
import { Step2ReligiousProfile } from "@/components/profile-creation/Step2ReligiousProfile";
import { Step3EducationCareer } from "@/components/profile-creation/Step3EducationCareer";
import { Step4LifestylePersonality } from "@/components/profile-creation/Step4LifestylePersonality";
import { Step5Preferences } from "@/components/profile-creation/Step5Preferences";
import { Step6ContactReview } from "@/components/profile-creation/Step6ContactReview";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import type { ProfileFormData } from "@/pages/ProfileCreation";
import { normalizePhone } from "@/lib/phone";

const DRAFT_KEY = "mas-request-join-draft-v1";

const STEPS = [
  { title: "Basic Info", description: "Tell us about yourself" },
  { title: "Religious Profile", description: "Your faith and practice" },
  { title: "Education & Career", description: "Your professional background" },
  { title: "Lifestyle & Personality", description: "Your interests and values" },
  { title: "What You're Looking For", description: "Your preferences" },
  { title: "Contact & Review", description: "Final details" },
];

const INITIAL_FORM_DATA: ProfileFormData = {
  first_name: "",
  age: 25,
  gender: "male",
  location: "",
  education_level: "",
  occupation: "",
  ethnicity: "Prefer not to say",
  citizenship_status: "US Citizen",
  marital_status: "Never Married",
  has_children: false,
  willing_to_relocate: false,
  religiosity_level: "moderate",
  prayer_frequency: "working on it",
  importance_of_islam: 5,
  sect: "Sunni",
  masjid_attendance: "Jumuah sometimes",
  quran_knowledge: "Basic",
  islamic_knowledge_level: "Basic",
  employment_status: "Employed full-time",
  financial_status: 3,
  personality_type: 5,
  personality_description: "",
  dietary_preferences: [],
  smoking: "Never",
  bio: "",
  seeking_age_min: 22,
  seeking_age_max: 35,
  seeking_ethnicity: [],
  seeking_sect: [],
  seeking_marital_status: [],
  seeking_children_ok: true,
  seeking_convert_ok: true,
  seeking_divorced_ok: true,
  seeking_religiosity: [],
  seeking_education: [],
  seeking_location_preference: "",
  seeking_description: "",
  email: "",
  phone: "",
  whatsapp: "",
  // Must match the contact_info CHECK constraint casing exactly.
  preferred_contact_method: "Email",
};

interface DraftShape {
  formData: ProfileFormData;
  currentStep: number;
}

function loadDraft(): DraftShape | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as DraftShape;
    if (!parsed || typeof parsed !== "object" || !parsed.formData) return null;
    return parsed;
  } catch {
    return null;
  }
}

function saveDraft(formData: ProfileFormData, currentStep: number) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(
      DRAFT_KEY,
      JSON.stringify({ formData, currentStep } satisfies DraftShape),
    );
  } catch {
    // Quota or serialization errors — silently ignore for MVP
  }
}

export default function RequestJoin() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<ProfileFormData>(INITIAL_FORM_DATA);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isEighteenPlus, setIsEighteenPlus] = useState(false);
  const [ageGateError, setAgeGateError] = useState<string | null>(null);

  // Restore draft on mount
  useEffect(() => {
    const draft = loadDraft();
    if (draft) {
      setFormData({ ...INITIAL_FORM_DATA, ...draft.formData });
      const restoredStep = Math.min(
        Math.max(draft.currentStep ?? 0, 0),
        STEPS.length - 1,
      );
      setCurrentStep(restoredStep);
    }
  }, []);

  const handleStepComplete = (stepData: Partial<ProfileFormData>) => {
    const updatedData = { ...formData, ...stepData };
    setFormData(updatedData);

    const nextStep =
      currentStep < STEPS.length - 1 ? currentStep + 1 : currentStep;
    saveDraft(updatedData, nextStep);

    if (currentStep < STEPS.length - 1) {
      setCurrentStep(nextStep);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      const prev = currentStep - 1;
      setCurrentStep(prev);
      saveDraft(formData, prev);
    }
  };

  const handleFinalSubmit = async (contactData: Partial<ProfileFormData>) => {
    const finalData = { ...formData, ...contactData };

    // Age-confirmation gate: require explicit 18+ checkbox AND age field >= 18
    if (!isEighteenPlus) {
      setAgeGateError(
        "Please confirm you are 18 years of age or older before submitting.",
      );
      return;
    }
    if (typeof finalData.age !== "number" || finalData.age < 18) {
      setAgeGateError(
        "MAS Matrimonial is for adults aged 18 and older. Please update your age in Step 1 to continue.",
      );
      return;
    }
    setAgeGateError(null);

    setSaving(true);

    try {
      // Strip out fields that don't belong in profile_data:
      // - contact fields (email, phone, whatsapp, preferred_contact_method)
      // - consent checkboxes (agree_terms, agree_privacy, understand_review, affirm_truthful)
      // - dealbreaker_questions (handled separately on the authed side)
      // - photo fields (deferred until approval)
      const {
        email,
        phone,
        whatsapp,
        preferred_contact_method,
        wali_name,
        wali_email,
        wali_phone,
        reveal_wali,
        agree_terms,
        agree_privacy,
        understand_review,
        affirm_truthful,
        dealbreaker_questions,
        profile_picture_url,
        profile_pictures,
        references,
        ...profileData
      } = finalData;
      // Note: community_connection stays in profileData — it's a profiles column.
      // references are sent as a sibling array (they become profile_references rows).

      const consent_given =
        finalData.agree_terms === true && finalData.agree_privacy === true;

      const { data, error } = await supabase.functions.invoke(
        "submit-join-request",
        {
          body: {
            consent_given,
            profile_data: profileData,
            contact_data: {
              email: finalData.email,
              // Phones are stored normalized (E.164 where parseable) — see @/lib/phone.
              phone: normalizePhone(finalData.phone),
              whatsapp: normalizePhone(finalData.whatsapp),
              preferred_contact_method: finalData.preferred_contact_method,
              wali_name: finalData.wali_name || null,
              wali_email: finalData.wali_email || null,
              wali_phone: normalizePhone(finalData.wali_phone),
              reveal_wali: finalData.reveal_wali ?? false,
            },
            references: (finalData.references ?? []).map((r) => ({
              name: r.name,
              relationship: r.relationship,
              phone: normalizePhone(r.phone),
              email: r.email || null,
            })),
          },
        },
      );

      if (error) throw error;
      if (data && data.success === false) {
        throw new Error(data.error || "Failed to submit request");
      }

      // Clear draft on success
      try {
        localStorage.removeItem(DRAFT_KEY);
      } catch {
        // ignore
      }

      setSubmitted(true);
    } catch (err: any) {
      console.error("join_request.submit_failed");
      toast.error(err?.message || "Failed to submit request");
    } finally {
      setSaving(false);
    }
  };

  const progressPercentage = ((currentStep + 1) / STEPS.length) * 100;

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Navbar />
        <div className="container mx-auto flex flex-1 items-center justify-center px-4 py-12">
          <Card className="w-full max-w-lg text-center">
            <CardHeader>
              <div className="mx-auto mb-2 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                <CheckCircle2 className="h-10 w-10 text-primary" />
              </div>
              <CardTitle className="text-2xl">Request submitted!</CardTitle>
              <CardDescription className="text-base">
                Thanks — your profile has been sent to our matchmakers for
                review. You'll receive an email when it's been approved
                (usually within a few days).
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => navigate("/")} className="w-full sm:w-auto">
                Back to home
              </Button>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />
      <div className="container mx-auto max-w-3xl px-4 py-12 flex-1">
        <div className="mb-8">
          <h1 className="mb-2 text-3xl font-bold text-foreground">
            Request to Join
          </h1>
          <p className="text-muted-foreground">
            Step {currentStep + 1} of {STEPS.length}: {STEPS[currentStep].title}
          </p>
          <div className="mt-4">
            <Progress value={progressPercentage} className="h-2" />
          </div>
        </div>

        {currentStep === 5 && (
          <div className="mb-6 space-y-3">
            <div className="flex items-start space-x-2 rounded-md border border-border bg-muted/30 p-4">
              <Checkbox
                id="is_eighteen_plus"
                checked={isEighteenPlus}
                onCheckedChange={(checked) => {
                  setIsEighteenPlus(checked === true);
                  if (checked === true) setAgeGateError(null);
                }}
              />
              <Label
                htmlFor="is_eighteen_plus"
                className="font-normal cursor-pointer leading-snug"
              >
                I confirm I am 18 years of age or older. MAS Matrimonial is for
                adults seeking marriage.
              </Label>
            </div>
            {ageGateError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{ageGateError}</AlertDescription>
              </Alert>
            )}
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle>{STEPS[currentStep].title}</CardTitle>
            <CardDescription>{STEPS[currentStep].description}</CardDescription>
          </CardHeader>
          <CardContent>
            {currentStep === 0 && (
              <Step1BasicInfo
                data={formData}
                onComplete={handleStepComplete}
                saving={saving}
                disablePhotoUpload={true}
                continueLabel="Continue"
              />
            )}

            {currentStep === 1 && (
              <Step2ReligiousProfile
                data={formData}
                onComplete={handleStepComplete}
                onBack={handleBack}
                saving={saving}
                continueLabel="Continue"
              />
            )}

            {currentStep === 2 && (
              <Step3EducationCareer
                data={formData}
                onComplete={handleStepComplete}
                onBack={handleBack}
                saving={saving}
                continueLabel="Continue"
              />
            )}

            {currentStep === 3 && (
              <Step4LifestylePersonality
                data={formData}
                onComplete={handleStepComplete}
                onBack={handleBack}
                saving={saving}
                continueLabel="Continue"
              />
            )}

            {currentStep === 4 && (
              <Step5Preferences
                data={formData}
                onComplete={handleStepComplete}
                onBack={handleBack}
                saving={saving}
                continueLabel="Continue"
              />
            )}

            {currentStep === 5 && (
              <Step6ContactReview
                data={formData}
                onComplete={handleFinalSubmit}
                onBack={handleBack}
                onEditStep={setCurrentStep}
                saving={saving}
                isEditingExisting={false}
                submitLabel="Submit Request"
                loadingLabel="Submitting..."
              />
            )}
          </CardContent>
        </Card>
      </div>
      <Footer />
    </div>
  );
}
